import type { AxiosInstance } from 'axios';

import type { FunctionalRequirements } from '@accio3d/shared-ui/printability/services/printability3dService';
import type { SortDir } from '@accio3d/shared-ui/components/ui/sort-direction-select';
import { fetchPaginatedList } from '@accio3d/shared-ui/services/pagination';
import type { PagePaginatedResponse, PaginationParams } from '@accio3d/shared-ui/types/pagination';

/**
 * One priced part on a purchaser-facing quote. Narrower than the supplier's
 * `QuoteLine` — the list mapper doesn't join the part, so there is no name here.
 */
export interface PurchaserQuoteLine {
  manufacturingRequirementId: string;
  quantity: number;
  unitPrice: number | null;
  subTotal: number | null;
  total: number | null;
}

// Mirrors PurchasersService.toQuoteListItem on the API. Money values are
// dollar floats (not cents) — format with formatDollars.
export interface PurchaserQuoteListItem {
  id: string;
  rfqId: string;
  supplierName: string;
  lines: PurchaserQuoteLine[];
  subTotal: number | null;
  total: number | null;
  shippingPrice: number | null;
  shippingFrom: string | null;
  /** Manufacturing process (e.g. "DMLS"); null on legacy quotes */
  processType: string | null;
  leadTimeDays: number;
  score: number;
  status: 'quoted' | 'accepted' | 'declined' | 'expired' | 'withdrawn';
  isLocked: boolean;
  quotedOn: string | null;
  submittedAt: string;
}

export interface QuoteStats {
  all: number;
  manufacturing: number;
  engineeringServices: number;
}

/**
 * One part on the quote's RFQ. Each entry structurally matches RfqSpec
 * (shared-ui/components/custom/RfqSpecCard.tsx) so it can be passed straight into that component.
 */
export interface QuoteRfqPartSpec {
  id: string;
  partId: string;
  partName: string | null;
  partNumber: string | null;
  /** Signed STL mesh from the latest COMPLETE 3D analysis; null without one. */
  previewUrl: string | null;
  /** Static list thumbnail, resolved wherever previewUrl is. */
  thumbnailUrl: string | null;
  material: string | null;
  processType: string | null;
  quantity: number;
  color: string | null;
  infill: number | null;
  postProcesses: string[];
  partLengthMm: number | null;
  partWidthMm: number | null;
  partHeightMm: number | null;
  partMassG: number | null;
  functionalRequirements: FunctionalRequirements | null;
}

export interface QuoteRfqSpec {
  name: string | null;
  rfqNumber: string;
  shippingAddress: string | null;
  isReconstruction: boolean;
  createdAt: string;
  manufacturingRequirements: QuoteRfqPartSpec[];
}

export interface PurchaserQuoteDetail extends PurchaserQuoteListItem {
  notes: string | null;
  rfq: QuoteRfqSpec;
}

export type QuoteStatusFilter = PurchaserQuoteListItem['status'];
export type QuoteServiceTypeFilter = 'manufacturing' | 'engineering_services';

export interface QuoteListFilters {
  search?: string;
  status?: QuoteStatusFilter;
  serviceType?: QuoteServiceTypeFilter;
  sortDir?: SortDir;
}

// Subset of the order response the checkout flow needs
export interface PurchaserOrder {
  id: string;
  orderNumber: string;
  paymentStatus: 'pending' | 'paid';
  paidAt: string | null;
  dueDate: string | null;
  total: number;
}

/**
 * A shipping rate the purchaser can pick at checkout (DSE-620). Mirrors
 * QuotesService.condenseRates on the API — the list is already condensed to
 * one option per carrier plus the fastest, with `cheapest`/`fastest` flags for
 * badging. `rate` is a dollar string as EasyPost returns it.
 *
 * Deliberately re-declared from the API's `quotes/types.ts`: there is no shared
 * type package spanning `server/api` and the portals, so every response shape in
 * `src/api/` is hand-written against the wire format (`OrderDocument` in
 * `api/supplier.ts` mirrors `orders/types.ts` the same way). Keep the two in step
 * by hand until an api-contracts package exists.
 */
export interface QuoteShippingRate {
  id: string;
  shipmentId: string;
  carrier: string;
  service: string;
  rate: string;
  currency: string;
  deliveryDays: number | null;
  cheapest: boolean;
  fastest: boolean;
}

/**
 * Destination address for a rate request, EasyPost-shaped. `country` is an ISO
 * 3166-1 alpha-2 code (e.g. "US"). Mirrors the API's addressSchema.
 */
export interface ShippingRateAddress {
  name?: string;
  street1: string;
  street2?: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

/** The purchaser's chosen rate, flattened onto the accept payload. */
export interface ShippingSelection {
  selectedShipmentId: string;
  selectedRateId: string;
  shippingCarrier: string;
  shippingService: string;
  shippingCost: number;
  shippingCurrency: string;
  estimatedDeliveryDays: number | null;
}

/**
 * Accept payload. The rate selection is spread in from {@link ShippingSelection}
 * rather than re-listed, so the two can't drift: it is present only when the
 * purchaser picked a live rate (DSE-620), and is all-or-nothing — the API
 * rejects a partial selection. `shipToAddress` is the structured destination
 * the rates were estimated for; the API snapshots it onto the order so the
 * bought label's destination is verifiable.
 */
export interface AcceptQuoteInput extends Partial<ShippingSelection> {
  shippingTo: string;
  shipToAddress?: ShippingRateAddress;
  notes?: string;
}

export function createQuotesService(client: AxiosInstance) {
  return {
    async list(
      pagination?: PaginationParams,
      filters?: QuoteListFilters,
    ): Promise<PagePaginatedResponse<PurchaserQuoteListItem>> {
      // Filtering/sorting is server-side; drop unset keys so the URL stays clean.
      const params = Object.fromEntries(
        Object.entries(filters ?? {}).filter(([, value]) => value != null && value !== ''),
      );
      return fetchPaginatedList<PurchaserQuoteListItem>(
        client,
        '/purchasers/quotes',
        pagination,
        Object.keys(params).length ? { params } : undefined,
      );
    },

    async stats(): Promise<QuoteStats> {
      const { data } = await client.get<QuoteStats>('/purchasers/quotes/stats');
      return data;
    },

    async get(quoteId: string): Promise<PurchaserQuoteDetail> {
      const { data } = await client.get<PurchaserQuoteDetail>(`/purchasers/quotes/${quoteId}`);
      return data;
    },

    async reject(quoteId: string): Promise<void> {
      await client.post(`/purchasers/quotes/${quoteId}/reject`);
    },

    /**
     * Live EasyPost rates for a quote, condensed to cheapest-per-carrier +
     * fastest (DSE-620). The destination is the checkout ship-to; ship-from and
     * parcel come from the quote server-side.
     */
    async shippingRates(
      quoteId: string,
      toAddress: ShippingRateAddress,
    ): Promise<QuoteShippingRate[]> {
      const { data } = await client.post<QuoteShippingRate[]>(
        `/purchasers/quotes/${quoteId}/shipping-rates`,
        { toAddress },
      );
      return data;
    },

    /** Accepts the quote and creates the order (payment still pending). */
    async accept(quoteId: string, body: AcceptQuoteInput): Promise<PurchaserOrder> {
      const { data } = await client.post<PurchaserOrder>(
        `/purchasers/quotes/${quoteId}/accept`,
        body,
      );
      return data;
    },
  };
}

export type QuotesService = ReturnType<typeof createQuotesService>;
